import React, { useState, useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { SearchSuggestions } from './SearchSuggestions'
import { SearchEngine } from '../../utils/search.utils'
import { FAQItem } from '../../types/support.types'

interface SearchBarProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  faqs?: FAQItem[]
  placeholder?: string
  className?: string
}

export const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  onSearchChange,
  faqs = [],
  placeholder = 'Search for questions...',
  className = ''
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Generate suggestions when query changes
  useEffect(() => {
    if (searchQuery.length >= 2 && faqs.length > 0) {
      const newSuggestions = SearchEngine.generateSuggestions(faqs, searchQuery, 5)
      setSuggestions(newSuggestions)
    } else {
      setSuggestions([])
    }
  }, [searchQuery, faqs])

  // Handle focus and blur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSuggestions(false)
        inputRef.current?.blur()
      }
    }

    if (showSuggestions) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showSuggestions])

  const handleFocus = () => {
    setIsFocused(true)
    setShowSuggestions(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onSearchChange(value)
    
    if (value.length >= 2) {
      setShowSuggestions(true)
    } else {
      setShowSuggestions(false)
    }
  }

  const handleClear = () => {
    onSearchChange('')
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  const handleSuggestionClick = (suggestion: string) => {
    onSearchChange(suggestion)
    setShowSuggestions(false)
    inputRef.current?.blur()
  }

  const handlePopularSearchClick = (search: string) => {
    onSearchChange(search)
    setShowSuggestions(false)
    inputRef.current?.blur()
  }

  const popularSearches = SearchEngine.getPopularSearches()
  const shouldShowSuggestions = showSuggestions && (suggestions.length > 0 || (!searchQuery && popularSearches.length > 0))

  return (
    <div ref={containerRef} className={`relative max-w-2xl mx-auto ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={`pl-11 pr-10 h-12 text-base border-2 transition-colors ${
            isFocused ? 'border-primary' : 'border-border'
          }`}
          autoComplete="off"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClear}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Search status */}
      {searchQuery && !shouldShowSuggestions && (
        <div className="text-sm text-muted-foreground mt-2 text-center">
          Searching for "{searchQuery}"
        </div>
      )}

      {/* Search Suggestions */}
      <SearchSuggestions
        suggestions={suggestions}
        popularSearches={searchQuery ? [] : popularSearches}
        onSuggestionClick={handleSuggestionClick}
        onPopularSearchClick={handlePopularSearchClick}
        isVisible={shouldShowSuggestions}
      />
    </div>
  )
}